import { z } from "zod"
import { Errors } from "../errors"
import { createFilterBySchema, oneOrMany } from "../shared/filter_dtos"
import { PaginationSchema } from "../shared/pagination_dtos"
import { QuerySchema } from "../shared/query_dtos"
import { createSortSchema } from "../shared/sort_dtos"
import { GoalMediaStrategyEnum, GoalMediaTypeEnum, GoalTypeEnum } from "./response_data"

const sortFields = ["name", "type", "createdAt"] as const
export const GoalsSortSchema = createSortSchema(sortFields, "createdAt", "desc")

export const GoalsFilterBySchema = createFilterBySchema([
  { key: "type", value: oneOrMany(z.enum(GoalTypeEnum)) },
  { key: "campaignId", value: oneOrMany(z.string()) },
  { key: "conversationId", value: z.string().optional() },
  { key: "withCanonicalTopics", value: z.string().optional() },
] as const)

export const GetGoalsSchema = PaginationSchema.extend(QuerySchema.shape)
  .extend(GoalsSortSchema.shape)
  .extend(GoalsFilterBySchema.shape)

export const GoalMediaSchema = z.object({
  order: z.number(),
  label: z.string().nullish(),
  link: z.string(),
  mediaType: GoalMediaTypeEnum,
})

export const CreateGoalSchema = z
  .object({
    name: z.string(),
    assistantInstructions: z.string().optional(),
    insightsInstructions: z.string().optional(),
    type: z.enum(GoalTypeEnum),
    goalOptions: z.array(z.string()).optional(),
    slug: z.string().optional(),
    shots: z.number().max(5).optional(),
    goalMedias: z.array(GoalMediaSchema).nullish(),
    mediaStrategy: z.enum(GoalMediaStrategyEnum).optional(),
    isUnderlying: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (
      !data.isUnderlying &&
      (!data.assistantInstructions || data.assistantInstructions.trim().length === 0)
    ) {
      ctx.addIssue({
        code: "custom",
        message: Errors.GOAL_ASSISTANT_INSTRUCTIONS_REQUIRED,
        path: ["assistantInstructions"],
      })
    }
    if (data.type === GoalTypeEnum.Predefined || data.type === GoalTypeEnum.NPS) {
      if (!data.goalOptions) {
        ctx.addIssue({
          code: "custom",
          message: Errors.GOAL_OPTIONS_REQUIRED,
          path: ["goalOptions"],
        })
      } else if (data.goalOptions.length < 2) {
        ctx.addIssue({
          code: "custom",
          message: Errors.GOAL_OPTIONS_MIN_REQUIRED,
          path: ["goalOptions"],
        })
      } else if (data.type !== GoalTypeEnum.NPS && data.goalOptions.length > 10) {
        ctx.addIssue({
          code: "custom",
          message: Errors.GOAL_OPTIONS_MAX_REQUIRED,
          path: ["goalOptions"],
        })
      } else if (data.type === GoalTypeEnum.NPS && data.goalOptions.length !== 11) {
        ctx.addIssue({
          code: "custom",
          message: Errors.GOAL_OPTIONS_NPS_REQUIRED,
          path: ["goalOptions"],
        })
      }
    }
  })

export const GetGoalSchema = z.object({
  id: z.string().transform(BigInt),
})

export const GetGoalActivitySchema = z.object({
  goalIds: z
    .array(z.string())
    .optional()
    .transform((ids) => ids?.map(BigInt) ?? []),
})

export const UploadGoalMediaFileSchema = z.object({
  file: z.object({
    data: z.unknown().refine((value) => Buffer.isBuffer(value), {
      error: "Expected a Buffer",
    }),
    name: z.string(),
    mimetype: z.string(),
  }),
})
